import React from 'react';
import { Tooltip } from 'antd';
import { DownOutlined, RightOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { CamProp, CAM_PROPS, CAM_PROP_COLOR, CAM_PROP_LABEL, ROW_H, SUB_H, FRAME_OFFSET } from './timelineConstants';

// ── CameraLabelRow ────────────────────────────────────────────────────────────
interface CameraLabelRowProps {
  camExpanded: boolean;
  displayFrame: number;
  cameraKeyframes: Record<number, { x: number; y: number; z: number }>;
  onToggleExpanded: () => void;
  onAddCamKf: (frame: number) => void;
  onRemoveCamKf: (frame: number) => void;
}

export const CameraLabelRow = React.memo(({
  camExpanded, displayFrame, cameraKeyframes, onToggleExpanded, onAddCamKf, onRemoveCamKf,
}: CameraLabelRowProps) => (
  <div style={{ borderBottom: '1px solid #1e1e2e' }}>
    <div style={{ height: ROW_H, display: 'flex', alignItems: 'center', gap: 4, padding: '0 5px', background: 'rgba(96,165,250,0.06)' }}>
      <span style={{ cursor: 'pointer', fontSize: 10, color: '#444', flexShrink: 0, width: 12, display: 'flex', alignItems: 'center' }} onClick={onToggleExpanded}>
        {camExpanded ? <DownOutlined /> : <RightOutlined />}
      </span>
      <VideoCameraOutlined style={{ color: '#60a5fa', fontSize: 11, flexShrink: 0 }} />
      <span style={{ flex: 1, fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#60a5fa' }}>Camera</span>
      <Tooltip title={displayFrame in cameraKeyframes ? 'Remove camera keyframe' : 'Add camera keyframe'}>
        <span style={{ cursor: 'pointer', flexShrink: 0, fontSize: 12, color: displayFrame in cameraKeyframes ? '#60a5fa' : '#333', lineHeight: 1, padding: '0 2px' }}
          onClick={() => displayFrame in cameraKeyframes ? onRemoveCamKf(displayFrame) : onAddCamKf(displayFrame)}>
          ◆
        </span>
      </Tooltip>
    </div>
    {camExpanded && CAM_PROPS.map((prop) => (
      <div key={prop} style={{ height: SUB_H, display: 'flex', alignItems: 'center', padding: '0 5px 0 18px', gap: 4, background: '#0a0a16' }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: CAM_PROP_COLOR[prop], flexShrink: 0 }} />
        <span style={{ fontSize: 10, color: '#555', flex: 1 }}>{CAM_PROP_LABEL[prop]}</span>
      </div>
    ))}
  </div>
));

// ── CameraTrackRow ────────────────────────────────────────────────────────────
interface CameraTrackRowProps {
  camExpanded: boolean;
  cameraKeyframes: Record<number, { x: number; y: number; z: number }>;
  camDragState: { prop: CamProp | 'summary'; fromFrame: number; toFrame: number } | null;
  pxPerFrame: number;
  scrollLeft: number;
  trackAreaW: number;
  onSetCurrentFrame: (frame: number) => void;
  onAddCamKf: (frame: number) => void;
  onRemoveCamKf: (frame: number) => void;
  onCamKfPointerDown: (e: React.PointerEvent, prop: CamProp | 'summary', frame: number) => void;
  onCamKfPointerMove: (e: React.PointerEvent) => void;
  onCamKfPointerUp: (e: React.PointerEvent, prop: CamProp | 'summary', frame: number) => void;
  frameFromTrack: (e: { clientX: number }) => number;
  camDragMovedRef: React.MutableRefObject<boolean>;
}

export const CameraTrackRow = React.memo(({
  camExpanded, cameraKeyframes, camDragState,
  pxPerFrame, scrollLeft, trackAreaW,
  onSetCurrentFrame, onAddCamKf, onRemoveCamKf,
  onCamKfPointerDown, onCamKfPointerMove, onCamKfPointerUp,
  frameFromTrack, camDragMovedRef,
}: CameraTrackRowProps) => {
  const camFrames = Object.keys(cameraKeyframes).map(Number).sort((a, b) => a - b);
  return (
    <div style={{ borderBottom: '1px solid #1e1e2e' }}>
      {/* Summary row */}
      <div
        style={{ height: ROW_H, position: 'relative', cursor: 'crosshair', background: 'rgba(96,165,250,0.03)' }}
        onClick={(e) => { if (!(e.target as HTMLElement).dataset.kf) onSetCurrentFrame(frameFromTrack(e)); }}
        onDoubleClick={(e) => { e.preventDefault(); const f = frameFromTrack(e); onSetCurrentFrame(f); onAddCamKf(f); }}
      >
        {camFrames.map((frame) => {
          const x = frame * pxPerFrame;
          if (x < scrollLeft - 10 || x > scrollLeft + trackAreaW + 10) return null;
          const isBeingDragged = camDragState?.prop === 'summary' && camDragState?.fromFrame === frame;
          const ghostX = isBeingDragged && camDragState!.toFrame !== frame ? camDragState!.toFrame * pxPerFrame : null;
          return (
            <React.Fragment key={frame}>
              <div data-kf="1"
                style={{ position: 'absolute', left: x, top: '50%', width: 7, height: 7, background: '#60a5fa', transform: 'translateX(-3.5px) translateY(-3.5px) rotate(45deg)', cursor: isBeingDragged ? 'grabbing' : 'grab', zIndex: 3, boxShadow: '0 0 3px #60a5fa66', opacity: isBeingDragged ? 0.3 : 1 }}
                title={`Cam f${frame + FRAME_OFFSET} — drag to move, dbl-click: remove`}
                onPointerDown={(e) => onCamKfPointerDown(e, 'summary', frame)}
                onPointerMove={onCamKfPointerMove}
                onPointerUp={(e) => onCamKfPointerUp(e, 'summary', frame)}
                onClick={(e) => { e.stopPropagation(); if (!camDragMovedRef.current) onSetCurrentFrame(frame); camDragMovedRef.current = false; }}
                onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveCamKf(frame); }}
              />
              {ghostX !== null && (<>
                <div style={{ position: 'absolute', left: ghostX, top: '50%', width: 7, height: 7, background: '#60a5fa', transform: 'translateX(-3.5px) translateY(-3.5px) rotate(45deg)', zIndex: 4, boxShadow: '0 0 6px #60a5fa', opacity: 0.9, outline: '1.5px solid white', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', left: ghostX, top: '50%', transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none', zIndex: 20, background: 'rgba(10,10,20,0.9)', color: '#f5a623', fontSize: 10, borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', border: '1px solid rgba(245,166,35,0.35)', lineHeight: '14px' }}>
                  {camDragState!.toFrame + FRAME_OFFSET}
                </div>
              </>)}
            </React.Fragment>
          );
        })}
      </div>
      {/* Per-axis sub-rows */}
      {camExpanded && CAM_PROPS.map((prop) => (
        <div key={prop}
          style={{ height: SUB_H, position: 'relative', cursor: 'crosshair', background: '#090914', borderTop: '1px solid #1a1a28' }}
          onClick={(e) => { if (!(e.target as HTMLElement).dataset.kf) onSetCurrentFrame(frameFromTrack(e)); }}
          onDoubleClick={(e) => { e.preventDefault(); const f = frameFromTrack(e); onSetCurrentFrame(f); onAddCamKf(f); }}
        >
          <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 1, background: `${CAM_PROP_COLOR[prop]}1a`, pointerEvents: 'none' }} />
          {camFrames.map((frame) => {
            const x = frame * pxPerFrame;
            if (x < scrollLeft - 8 || x > scrollLeft + trackAreaW + 8) return null;
            const isBeingDragged = camDragState?.prop === prop && camDragState?.fromFrame === frame;
            const ghostX = isBeingDragged && camDragState!.toFrame !== frame ? camDragState!.toFrame * pxPerFrame : null;
            return (
              <React.Fragment key={frame}>
                <div data-kf="1"
                  style={{ position: 'absolute', left: x, top: '50%', width: 6, height: 6, background: CAM_PROP_COLOR[prop], transform: 'translateX(-3px) translateY(-3px) rotate(45deg)', cursor: isBeingDragged ? 'grabbing' : 'grab', zIndex: 3, boxShadow: `0 0 3px ${CAM_PROP_COLOR[prop]}88`, opacity: isBeingDragged ? 0.3 : 1 }}
                  title={`Cam ${CAM_PROP_LABEL[prop]} f${frame + FRAME_OFFSET}`}
                  onPointerDown={(e) => onCamKfPointerDown(e, prop, frame)}
                  onPointerMove={onCamKfPointerMove}
                  onPointerUp={(e) => onCamKfPointerUp(e, prop, frame)}
                  onClick={(e) => { e.stopPropagation(); if (!camDragMovedRef.current) onSetCurrentFrame(frame); camDragMovedRef.current = false; }}
                  onDoubleClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveCamKf(frame); }}
                />
                {ghostX !== null && (<>
                  <div style={{ position: 'absolute', left: ghostX, top: '50%', width: 6, height: 6, background: CAM_PROP_COLOR[prop], transform: 'translateX(-3px) translateY(-3px) rotate(45deg)', zIndex: 4, boxShadow: `0 0 6px ${CAM_PROP_COLOR[prop]}`, opacity: 0.9, outline: '1.5px solid white', pointerEvents: 'none' }} />
                  <div style={{ position: 'absolute', left: ghostX, top: '50%', transform: 'translateX(-50%) translateY(-50%)', pointerEvents: 'none', zIndex: 20, background: 'rgba(10,10,20,0.9)', color: '#f5a623', fontSize: 10, borderRadius: 3, padding: '1px 4px', whiteSpace: 'nowrap', border: '1px solid rgba(245,166,35,0.35)', lineHeight: '14px' }}>
                    {camDragState!.toFrame + FRAME_OFFSET}
                  </div>
                </>)}
              </React.Fragment>
            );
          })}
        </div>
      ))}
    </div>
  );
});
